import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { message, Tooltip } from 'antd';
import {
  AimOutlined,
  ArrowRightOutlined,
  CloseOutlined,
  MinusOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SyncOutlined,
} from '@ant-design/icons';

import PageTitleBar from '@iso/components/utility/PageTitleBar';
import { b64_to_utf8, formatMoney } from '@iso/lib/helpers/utility';
import apiBoard from '@iso/services/repository/board';

import ForceGraph from './engine/ForceGraph';
import {
  OPERACAO_NETWORK,
  OPERACAO_TYPE_INFO,
  OPERACAO_TYPE_LABELS,
} from './engine/networkConfig';
import {
  Wrap,
  CanvasLayer,
  SearchBox,
  TopRight,
  RightRail,
  FilterPanel,
  LevelBar,
  LevelButtons,
  LevelButton,
  FilterTitle,
  FilterList,
  FilterSection,
  FilterFooter,
  FilterActions,
  LegendItem,
  Controls,
  ControlButton,
  Panel,
  PanelHead,
  PanelBody,
  PanelMeta,
  Tag,
  ActionRow,
  ActionButton,
  CenterOverlay,
  Spinner,
  Empty,
} from './Confluencia.styles';

const MAX_ACTIVITIES = OPERACAO_NETWORK.caps.maxActivities;
const OVERVIEW_ACTIVITIES = OPERACAO_NETWORK.caps.overviewActivities;

// Paleta geral menos vibrante (departamentos sem cor definida).
const PALETTE = [
  '#6f9bbf', '#8b84ad', '#b08394', '#6f9f98', '#a58f6f',
  '#a9745f', '#7e8b99', '#6f9bbf', '#8b84ad', '#b08394',
  '#6f9f98', '#a58f6f',
];

// Suaviza a cor do departamento (mistura com cinza-azulado).
const muteColor = (hex) => {
  if (typeof hex !== 'string') return hex;
  const clean = hex.replace('#', '').trim();
  if (!/^[0-9a-f]{6}$/i.test(clean)) return hex;
  const n = parseInt(clean, 16);
  const mix = (v) => Math.round(v * 0.62 + 138 * 0.38).toString(16).padStart(2, '0');
  return '#' + mix((n >> 16) & 255) + mix((n >> 8) & 255) + mix(n & 255);
};

// Cache local do grafo (por empresa + usuário) — 15 minutos.
const CACHE_TTL = 15 * 60 * 1000;
const CACHE_KEY = () =>
  `confluencia:${OPERACAO_NETWORK.id}:v1:${sessionStorage.getItem('domain') || ''}:${sessionStorage.getItem('userName') || ''}`;

const TYPE_INFO = OPERACAO_TYPE_INFO;
const TYPE_LABELS = OPERACAO_TYPE_LABELS;

const valueName = (value) =>
  value && typeof value === 'object' ? value.name || value.fantasyName || '' : '';
const valueId = (value) =>
  value && typeof value === 'object' ? value._id || value.id || null : value || null;

const normalizeColor = (value) => {
  if (typeof value === 'string' && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim())) return value.trim();
  return null;
};

const readSession = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return [];
    return String(b64_to_utf8(raw) || '').split(',').filter(Boolean);
  } catch (error) {
    return [];
  }
};

const deptNodeId = (boardId) => 'dep:' + boardId;

export default function Confluencia() {
  const history = useHistory();
  const rootRef = useRef(null);
  const graphRef = useRef(null);
  const seededRef = useRef(false);
  const enrichedRef = useRef(new Set());

  const [height, setHeight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [boardsInfo, setBoardsInfo] = useState({});
  const [activities, setActivities] = useState({});
  const [activityDetails, setActivityDetails] = useState({});
  // NÍVEIS: 1 departamentos · 2 etapas · 3 atividades+contatos · 4 tarefas · 5 vínculos
  const [maxLevel, setMaxLevel] = useState(3);
  // Expansão LOCAL (+1 nível) por item clicado, sem mexer no nível global.
  const [expandedNodes, setExpandedNodes] = useState(() => new Set());

  // Garante o padrão ao ABRIR a tela: nível 3, sem expansões locais herdadas.
  useEffect(() => {
    setMaxLevel(3);
    setExpandedNodes(new Set());
    setFocusBoard(null);
    setSelectedId(null);
  }, []);
  const [busy, setBusy] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [focusBoard, setFocusBoard] = useState(null);
  const [search, setSearch] = useState('');
  const [spinning, setSpinning] = useState(false);
  const [hiddenTypes, setHiddenTypes] = useState(() => new Set());

  const perms = useMemo(() => readSession('permissions'), []);
  const isAdmin = perms.includes('all');
  const boardsAccess = useMemo(() => readSession('boards_access'), []);
  const canOpenPeople = isAdmin || perms.includes('Pessoa_editar');
  const canOpenProduct = isAdmin || perms.includes('Produto_editar');

  // ---------- altura: ocupa a janela da mesa (ou a viewport fora dela) ----------
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const scroller = el.closest('.mesaJanelaConteudo') || el.closest('.isomorphicContent');
    const measure = () => {
      const h = scroller ? scroller.clientHeight : Math.max(420, window.innerHeight - 120);
      setHeight(h > 0 ? h : Math.max(420, window.innerHeight - 120));
    };
    measure();
    let ro = null;
    if (scroller && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(scroller);
    }
    window.addEventListener('resize', measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  // ---------- dados (mesma origem e mesmas permissões da tela de Quadros) ----------
  const loadDepartments = useCallback((background = false) => {
    if (!background) setLoading(true);
    setError(null);
    Promise.all([
      apiBoard.getAllInfo(),
      // Integrações entre departamentos (origem -> destino). Se falhar, o mapa
      // continua: só não desenha as linhas entre departamentos.
      apiBoard.getAllIntegrations().catch(() => ({ data: [] })),
    ])
      .then(([infoRes, intRes]) => {
        const list = Array.isArray(infoRes.data) ? infoRes.data : [];
        const allowed = isAdmin
          ? list
          : list.filter((board) => boardsAccess.includes(String(board._id)));
        setDepartments(allowed);
        setIntegrations(Array.isArray(intRes.data) ? intRes.data : []);
      })
      .catch((err) => {
        console.error(err);
        setError('Não foi possível carregar os departamentos.');
      })
      .finally(() => {
        if (!background) setLoading(false);
      });
  }, [isAdmin, boardsAccess]);

  // CACHE local (stale-while-revalidate): mostra na hora o último snapshot
  // e atualiza em segundo plano. Chave por empresa + usuário, TTL curto.
  useEffect(() => {
    let hasCache = false;
    try {
      const raw = localStorage.getItem(CACHE_KEY());
      if (raw) {
        const cache = JSON.parse(raw);
        if (cache && cache.t && Date.now() - cache.t < CACHE_TTL && Array.isArray(cache.d)) {
          const list = isAdmin
            ? cache.d
            : cache.d.filter((board) => boardsAccess.includes(String(board._id)));
          const allowedIds = new Set(list.map((board) => String(board._id)));
          const acts = {};
          Object.keys(cache.acts || {}).forEach((id) => {
            if (allowedIds.has(String(id))) acts[id] = cache.acts[id];
          });
          setDepartments(list);
          setIntegrations(Array.isArray(cache.i) ? cache.i : []);
          setActivities(acts);
          setBoardsInfo(cache.info || {});
          setLoading(false);
          hasCache = true;
        }
      }
    } catch (error) {
      /* cache corrompido: ignora */
    }
    loadDepartments(hasCache);
  }, [loadDepartments, isAdmin, boardsAccess]);

  // Grava o snapshot (com debounce) para a próxima abertura ser instantânea.
  useEffect(() => {
    if (!departments.length) return undefined;
    const timer = window.setTimeout(() => {
      try {
        const json = JSON.stringify({
          t: Date.now(),
          d: departments,
          i: integrations,
          acts: activities,
          info: boardsInfo,
        });
        if (json.length < 4000000) localStorage.setItem(CACHE_KEY(), json);
      } catch (error) {
        /* sem espaço/permissão: segue sem cache */
      }
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [departments, integrations, activities, boardsInfo]);

  // A lista do quadro não carrega a OS completa de cada atividade; o detalhe
  // carrega. Busca em segundo plano (4 por vez) para as tarefas aparecerem.
  const enrichBoard = useCallback((boardId, list) => {
    if (!boardId || !Array.isArray(list) || !list.length) return;
    if (enrichedRef.current.has(boardId)) return;
    const targets = list.filter((activity) => !activity.orderService).slice(0, 40);
    if (!targets.length) return;
    enrichedRef.current.add(boardId);

    let cursor = 0;
    const patch = {};
    const worker = async () => {
      while (cursor < targets.length) {
        const index = cursor;
        cursor += 1;
        const activity = targets[index];
        try {
          const response = await apiBoard.getBoardData(boardId, 'activitie', activity._id);
          if (response && response.data && response.data.orderService) {
            patch[activity._id] = response.data;
          }
        } catch (error) {
          /* quadro sem OS ou sem permissão: segue sem tarefas */
        }
      }
    };

    Promise.all([worker(), worker(), worker(), worker()]).then(() => {
      if (Object.keys(patch).length) {
        setActivityDetails((prev) => ({
          ...prev,
          [boardId]: { ...(prev[boardId] || {}), ...patch },
        }));
      }
    });
  }, []);

  // Busca o detalhe (OS) de UMA atividade quando o item é expandido.
  const enrichActivity = useCallback((boardId, activity) => {
    if (!boardId || !activity || !activity._id || activity.orderService) return;
    apiBoard
      .getBoardData(boardId, 'activitie', activity._id)
      .then((response) => {
        if (response && response.data && response.data.orderService) {
          setActivityDetails((prev) => ({
            ...prev,
            [boardId]: { ...(prev[boardId] || {}), [activity._id]: response.data },
          }));
        }
      })
      .catch(() => {});
  }, []);

  const toggleNode = useCallback((id) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const loadBoardActivities = useCallback((boardId, options) => {
    const focusAfter = !options || options.focus !== false;
    setBusy((prev) => ({ ...prev, [boardId]: true }));
    return Promise.all([
      apiBoard.getBoardData(boardId, 'initial'),
      apiBoard.getBoardData(boardId, 'activities'),
    ])
      .then(([infoRes, actRes]) => {
        setBoardsInfo((prev) => ({ ...prev, [boardId]: infoRes.data || {} }));
        setActivities((prev) => ({
          ...prev,
          [boardId]: Array.isArray(actRes.data) ? actRes.data : [],
        }));
        if (focusAfter) {
          window.setTimeout(() => {
            if (graphRef.current) graphRef.current.focus(deptNodeId(boardId));
          }, 140);
        }
      })
      .catch((err) => {
        console.error(err);
        message.error('Não foi possível carregar as atividades deste departamento.');
      })
      .finally(() => {
        setBusy((prev) => {
          const next = { ...prev };
          delete next[boardId];
          return next;
        });
      });
  }, []);

  // O GLOBO INTEIRO usa o nível principal selecionado (sem nível local por item).
  const levelOf = useCallback(() => maxLevel, [maxLevel]);

  const applyLevel = useCallback((level) => {
    setMaxLevel(level);
    setExpandedNodes(new Set());
    setFocusBoard(null);
    setSelectedId(null);
  }, []);

  // PRÉ-CARREGAMENTO EM FASES (sem travar a tela):
  // 1) departamentos já na esfera; 2) busca todas as etapas e forma a esfera
  // do nível 2; 3) busca todas as atividades e fecha a esfera do nível 3.
  useEffect(() => {
    if (seededRef.current || !departments.length) return;
    seededRef.current = true;
    const ids = departments.map((board) => board._id);

    Promise.all(ids.map((id) => apiBoard.getBoardData(id, 'initial').catch(() => null)))
      .then((infos) => {
        const infoPatch = {};
        infos.forEach((info, index) => {
          if (info && info.data) infoPatch[ids[index]] = info.data;
        });
        if (Object.keys(infoPatch).length) {
          setBoardsInfo((prev) => ({ ...prev, ...infoPatch }));
        }
        // Só depois que as etapas entram, busca as atividades do nível 3.
        return Promise.all(
          ids.map((id) => apiBoard.getBoardData(id, 'activities').catch(() => null)),
        );
      })
      .then((actsList) => {
        const actPatch = {};
        actsList.forEach((acts, index) => {
          if (acts && Array.isArray(acts.data)) actPatch[ids[index]] = acts.data;
        });
        if (Object.keys(actPatch).length) {
          setActivities((prev) => ({ ...prev, ...actPatch }));
        }
      });
  }, [departments]);

  // Nível 4/5 vale para o GLOBO INTEIRO: busca a OS de todos os departamentos.
  useEffect(() => {
    if (maxLevel < 4) return;
    departments.forEach((board) => enrichBoard(board._id, activities[board._id] || []));
  }, [maxLevel, departments, activities, enrichBoard]);

  // Integrações entre departamentos: dedupe por par origem→destino.
  const integrationData = useMemo(() => {
    const pairMap = new Map();
    (integrations || []).forEach((board) => {
      const source = String(board._id);
      (board.integrations || []).forEach((integration) => {
        const target = String(integration.destBoard || '');
        if (!target || target === 'undefined' || target === source) return;
        const key = source + '>' + target;
        const current = pairMap.get(key) || { source, target, count: 0 };
        current.count += 1;
        pairMap.set(key, current);
      });
    });
    const edges = Array.from(pairMap.values());
    const out = new Map();
    const inc = new Map();
    edges.forEach((edge) => {
      out.set(edge.source, (out.get(edge.source) || 0) + edge.count);
      inc.set(edge.target, (inc.get(edge.target) || 0) + edge.count);
    });
    return { edges, out, inc };
  }, [integrations]);

  // ---------- grafo montado a partir dos dados reais ----------
  const graph = useMemo(() => {
    const nodes = [];
    const links = [];
    const pendingChain = [];
    const pendingTaskChain = [];
    const nodeIds = new Set();

    const nameOf = (value) => {
      if (value && typeof value === 'object') return value.name || value.fantasyName || '';
      return typeof value === 'string' ? value : '';
    };
    const idOf = (value) => {
      if (value && typeof value === 'object') return value._id || value.id || null;
      return value || null;
    };

    const nodeById = new Map();
    const addNode = (node) => {
      const existing = nodeById.get(node.id);
      if (existing) {
        Object.assign(existing, node);
        return;
      }
      nodeById.set(node.id, node);
      nodeIds.add(node.id);
      nodes.push(node);
    };
    const addLink = (source, target, opts) => {
      if (!nodeIds.has(source) || !nodeIds.has(target)) return;
      links.push({ source, target, ...opts });
    };

    departments.forEach((board, index) => {
      // Cor por NÍVEL: 1 azul · 2 azul claro · 3 branco · 4 amarelo · 5 verde.
      const color = '#3b82f6';
      const boardId = board._id;
      const level = levelOf(boardId);
      addNode({
        id: deptNodeId(boardId),
        label: board.name || 'Departamento',
        type: 'departamento',
        color,
        size: 15 + Math.min(2, Math.log2((Number(board.numTicket) || 0) + 1)),
        count: Number(board.numTicket) || 0,
        intIn: integrationData.inc.get(String(boardId)) || 0,
        intOut: integrationData.out.get(String(boardId)) || 0,
        expandable: true,
        expanded: level > 1,
        level,
        boardId,
        data: board,
      });

      if (level < 2) return;

      const all = activities[boardId] || [];
      const info = boardsInfo[boardId] || {};
      const stages = Array.isArray(info.stages) ? info.stages : [];
      const stageName = (activity) => {
        const current = Array.isArray(activity.stages) ? activity.stages[0] : null;
        if (!current) return '';
        const found = stages.find((stage) => String(stage._id) === String(current.stage_id));
        return found ? found.name : '';
      };

      const showStages = !hiddenTypes.has('etapa');
      const stageById = new Map();
      if (showStages) {
        stages.forEach((stage) => {
          const stageNodeId = 'stage:' + boardId + ':' + stage._id;
          stageById.set(String(stage._id), stageNodeId);
          addNode({
            id: stageNodeId,
            label: stage.name || 'Etapa',
            type: 'etapa',
            color: '#7dd3fc',
            size: 10,
            boardId,
            data: stage,
          });
          addLink(deptNodeId(boardId), stageNodeId, { color: '#7e8b99', len: 90, spring: 0.034 });
        });
      }

      // Um clique numa etapa abre +1 nível SÓ para aquela etapa (sem mudar o global).
      const anyStageExpanded = stages.some((stage) =>
        expandedNodes.has('stage:' + boardId + ':' + stage._id),
      );
      if (level < 3 && !anyStageExpanded) return;
      const visible = all.slice(
        0,
        level >= 4 || anyStageExpanded ? MAX_ACTIVITIES : OVERVIEW_ACTIVITIES,
      );
      visible.forEach((activity) => {
        const activityId = 'act:' + activity._id;
        const label =
          activity.title ||
          (activity.controlNumber ? '#' + activity.controlNumber : '') ||
          (activity.client && activity.client.name) ||
          'Atividade';
        addNode({
          id: activityId,
          label,
          type: 'atividade',
          color: '#e2e8f0',
          size: 7.5,
          boardId,
          sub: stageName(activity),
          data: activity,
        });
        const currentStage = Array.isArray(activity.stages) ? activity.stages[0] : null;
        const stageNodeId = currentStage ? stageById.get(String(currentStage.stage_id)) : null;
        if (stageNodeId) addLink(stageNodeId, activityId, { color: '#8e9aa4', len: 70, spring: 0.052 });
        else addLink(deptNodeId(boardId), activityId, { color: '#8e9aa4', len: 112, spring: 0.032 });

        const client = activity.client;
        const clientId = client && (client._id || (typeof client === 'string' ? client : null));
        if (clientId) {
          addNode({
            id: 'cli:' + clientId,
            label: (client && client.name) || (client && client.fantasyName) || 'Cliente',
            type: 'cliente',
            color: '#e2e8f0',
            size: 6.5,
            data: typeof client === 'string' ? { _id: client } : client,
          });
          addLink(activityId, 'cli:' + clientId, { color: '#b08394', len: 64, spring: 0.052 });
        }

        const products = Array.isArray(activity.productsAndServices) ? activity.productsAndServices : [];
        products.forEach((product) => {
          const rawId = product.item || product.name;
          if (!rawId) return;
          const productId = 'prd:' + rawId;
          addNode({
            id: productId,
            label: product.name || 'Item',
            type: 'produto',
            color: '#e2e8f0',
            size: 6,
            data: product,
          });
          addLink(activityId, productId, { color: '#6f9f98', len: 52, spring: 0.055 });
        });

        // ---- TAREFAS da OS da atividade (só no departamento ABERTO) ----
        // A atividade já traz orderService.tasksAndItens quando o quadro tem OS.
        // Clique numa atividade abre +1 nível SÓ para ela (mostra as tarefas).
        const showTasks = level >= 4 || expandedNodes.has(activityId);
        if (!showTasks) return;
        // O detalhe (buscado em segundo plano) é a fonte completa da OS.
        const detail = (activityDetails[boardId] || {})[activity._id];
        const osSource = (detail && detail.orderService) || activity.orderService;
        const os = Array.isArray(osSource) ? osSource[0] : osSource;
        const tasks = os && Array.isArray(os.tasksAndItens) ? os.tasksAndItens : [];
        const taskNodeByKey = new Map();
        tasks.slice(0, 30).forEach((task, taskIndex) => {
          if (!task) return;
          const taskName = nameOf(task.task) || nameOf(task.item) || task.name || 'Tarefa';
          const rawTaskId = idOf(task.task) || task.key || taskIndex;
          const taskId = 'task:' + activity._id + ':' + rawTaskId;
          addNode({
            id: taskId,
            label: taskName,
            type: 'tarefa',
            color: '#f2c14e',
            size: 5,
            boardId,
            activityId: activity._id,
            data: task,
            sub: task.status || '',
          });
          addLink(activityId, taskId, { color: '#8b84ad', len: 44, spring: 0.068 });

          if (level >= 5 || expandedNodes.has(taskId)) {
            const supplierName = nameOf(task.realizedBy);
            const supplierId = idOf(task.realizedBy);
            if (supplierId) {
              addNode({
                id: 'sup:' + supplierId,
                label: supplierName || 'Fornecedor',
                type: 'fornecedor',
                color: '#63c08d',
                size: 4,
                data: typeof task.realizedBy === 'object' ? task.realizedBy : { _id: supplierId },
              });
              addLink(taskId, 'sup:' + supplierId, { color: '#a9745f', len: 38, spring: 0.052 });
            }

          }

          // Tarefa que GERA outra atividade (outro departamento): a ligação
          // existe mesmo que a atividade destino ainda não esteja carregada.
          if (task.activitie_id) {
            pendingChain.push({
              from: taskId,
              to: 'act:' + task.activitie_id,
              destBoard: task.destBoard,
            });
          }
          if (task.key != null) taskNodeByKey.set(String(task.key), taskId);
        });

        // Encadeamento de tarefas: passos do orderService.chaining.
        if (taskNodeByKey.size) {
          let chainSteps = [];
          try {
            const raw = os && os.chaining;
            chainSteps = Array.isArray(raw) ? raw : JSON.parse(raw || '[]');
          } catch (error) {
            chainSteps = [];
          }
          if (Array.isArray(chainSteps)) {
            for (let step = 0; step < chainSteps.length - 1; step += 1) {
              const fromKeys = (chainSteps[step].tasks || []).slice(0, 3);
              const toKeys = (chainSteps[step + 1].tasks || []).slice(0, 3);
              fromKeys.forEach((fromKey) => {
                toKeys.forEach((toKey) => {
                  const from = taskNodeByKey.get(String(fromKey));
                  const to = taskNodeByKey.get(String(toKey));
                  if (from && to) pendingTaskChain.push({ from, to });
                });
              });
            }
          }
        }
      });

      if (level >= 4 && all.length > visible.length) {
        const rest = all.length - visible.length;
        const moreId = 'more:' + boardId;
        addNode({
          id: moreId,
          label: '+' + rest + ' atividades',
          type: 'atividade',
          color: '#e2e8f0',
          size: 6,
          boardId,
          data: { __more: true, boardId, total: all.length, shown: visible.length },
        });
        addLink(deptNodeId(boardId), moreId, { color, len: 120, spring: 0.03 });
      }
    });

    // Integrações: departamento -> departamento (linha tracejada com seta).
    integrationData.edges.forEach((edge) => {
      addLink(deptNodeId(edge.source), deptNodeId(edge.target), {
        color: '#f2c14e',
        len: 240,
        spring: 0.004,
        dashed: true,
        arrow: true,
        kind: 'integracao',
        count: edge.count,
      });
    });

    // Encadeamentos: tarefa -> atividade gerada (pode estar em outro quadro).
    // Se a atividade destino não estiver carregada, cria a ponta para a
    // ligação NÃO desaparecer (o clique nela pode carregar o detalhe depois).
    pendingChain.forEach(({ from, to, destBoard }) => {
      if (!nodeIds.has(to)) {
        addNode({
          id: to,
          label: 'Atividade encadeada',
          type: 'atividade',
          color: '#e2e8f0',
          size: 6,
          boardId: destBoard || undefined,
          data: { __stub: true },
        });
      }
      if (nodeIds.has(from) && nodeIds.has(to)) {
        links.push({
          source: from,
          target: to,
          color: '#cbd5e1',
          len: 120,
          spring: 0.015,
          arrow: true,
          kind: 'encadeamento',
        });
      }
    });

    // Encadeamento de TAREFAS (passos do orderService.chaining).
    pendingTaskChain.forEach(({ from, to }) => {
      if (nodeIds.has(from) && nodeIds.has(to)) {
        links.push({
          source: from,
          target: to,
          color: '#8b84ad',
          len: 70,
          spring: 0.02,
          kind: 'chain-task',
        });
      }
    });

    return { nodes, links };
  }, [departments, levelOf, activities, activityDetails, boardsInfo, integrationData, hiddenTypes, expandedNodes]);

  const visibleGraph = useMemo(() => {
    if (!hiddenTypes.size) return graph;
    const nodes = graph.nodes.filter((node) => !hiddenTypes.has(node.type));
    const ids = new Set(nodes.map((node) => node.id));
    const links = graph.links.filter((link) => ids.has(link.source) && ids.has(link.target));
    return { nodes, links };
  }, [graph, hiddenTypes]);

  const counts = useMemo(() => {
    const total = { departamento: 0, etapa: 0, atividade: 0, tarefa: 0, cliente: 0, fornecedor: 0, produto: 0 };
    graph.nodes.forEach((node) => {
      if (total[node.type] !== undefined) total[node.type] += 1;
    });
    return total;
  }, [graph]);

  const selected = useMemo(
    () => graph.nodes.find((node) => node.id === selectedId) || null,
    [graph, selectedId],
  );

  const handleSearchKey = (event) => {
    if (event.key !== 'Enter') return;
    const term = search.trim().toLowerCase();
    if (!term) return;
    const found = visibleGraph.nodes.find((node) => String(node.label || '').toLowerCase().includes(term));
    if (found) {
      setSelectedId(found.id);
      if (graphRef.current) graphRef.current.focus(found.id);
    }
  };

  const clearSearch = () => setSearch('');

  const openBoard = (boardId) => {
    if (!boardId) return;
    // /board/:id = quadro de ATIVIDADES (PageBoard).
    // /dashboard/scrum-board/:id abria a edição/configuração do quadro.
    history.push('/board/' + boardId);
  };

  // Abre o quadro já com o modal da atividade (PageBoard lê ?activity=).
  const openActivity = (boardId, activityId) => {
    if (!boardId || !activityId) return;
    history.push('/board/' + boardId + '?activity=' + encodeURIComponent(activityId));
  };

  const openPeople = (personId) => {
    if (!personId) return;
    history.push('/dashboard/people/' + personId);
  };

  const openProduct = (productId) => {
    if (!productId) return;
    history.push('/dashboard/product/' + productId);
  };

  const toggleType = (type) => {
    setHiddenTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const collapseAll = () => {
    applyLevel(1);
    setSelectedId(null);
    setFocusBoard(null);
  };

  // Clique no departamento = sobe um nível e gira/centraliza o grupo.
  const handleSelectNode = useCallback(
    (node) => {
      setSelectedId(node ? node.id : null);
      if (!node) {
        setFocusBoard(null);
        return;
      }
      // Qualquer item (inclusive departamento): só seleciona e centraliza.
      // O nível é SEMPRE o principal escolhido no seletor (globo inteiro).
      if (graphRef.current) graphRef.current.focus(node.id);
    },
    [],
  );

  const renderFilterItem = (type) => (
    <LegendItem
      key={type}
      type="button"
      $color={TYPE_INFO[type].color}
      $off={hiddenTypes.has(type) ? 1 : 0}
      onClick={() => toggleType(type)}
      title={hiddenTypes.has(type) ? 'Mostrar camada' : 'Ocultar camada'}
    >
      <i />
      {TYPE_INFO[type].label}
      <b>{counts[type]}</b>
    </LegendItem>
  );

  // O nível do globo inteiro é o principal selecionado no seletor.
  const deepestLevel = maxLevel;
  const levelScale = [1.18, 1, 0.85, 0.72, 0.6][deepestLevel - 1] || 0.7;
  // DENSIDADE: quanto mais nós na tela, menores as bolinhas (e vice-versa).
  const densityScale = Math.min(
    1.2,
    Math.max(0.55, Math.pow(180 / Math.max(1, visibleGraph.nodes.length), 0.18)),
  );
  const sizeScale = Math.min(1.3, Math.max(0.45, levelScale * densityScale));

  const selectedBoardId = selected && (selected.boardId || (selected.data && selected.data.boardId));
  const selectedRaw = selected && selected.data;

  return (
    <>
      <PageTitleBar title="Confluência - Rede funcional da operação" backTo="/dashboard" />
      <Wrap
        ref={rootRef}
        style={{ height: height ? height + 'px' : 'calc(100vh - 110px)' }}
      >
        <CanvasLayer>
          <ForceGraph
            ref={graphRef}
            graph={visibleGraph}
            selectedId={selectedId}
            search={search}
            focusBoardId={focusBoard}
            levelScale={sizeScale}
            onSelect={handleSelectNode}
          />
        </CanvasLayer>

        <TopRight $panel={false}>
          <SearchBox $panel={false}>
          <SearchOutlined />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={handleSearchKey}
            placeholder="Buscar no mapa…"
            aria-label="Buscar no mapa"
          />
          {search ? (
            <button type="button" onClick={clearSearch} aria-label="Limpar busca">
              <CloseOutlined />
            </button>
          ) : null}
          </SearchBox>
        </TopRight>

        <RightRail $panel={false}>
          <FilterPanel>
          <LevelBar>
            <span>Nível</span>
            <LevelButtons>
              {[1, 2, 3, 4, 5].map((level) => (
                <LevelButton
                  key={level}
                  type="button"
                  $active={level <= maxLevel ? 1 : 0}
                  $current={maxLevel === level ? 1 : 0}
                  $level={level}
                  onClick={() => applyLevel(level)}
                  title={
                    [
                      '1 · Departamentos',
                      '2 · Etapas',
                      '3 · Atividades e contatos',
                      '4 · Tarefas',
                      '5 · Vínculos da tarefa',
                    ][level - 1]
                  }
                >
                  {level}
                </LevelButton>
              ))}
            </LevelButtons>
          </LevelBar>
          <FilterTitle>
            <strong>Filtros</strong>
            <small>{visibleGraph.nodes.length} nós</small>
          </FilterTitle>
          <FilterList>
            {['departamento', 'etapa', 'atividade', 'tarefa'].map(renderFilterItem)}
          </FilterList>
          <FilterSection>
            <small>Outros</small>
            <FilterList>
              {['cliente', 'fornecedor', 'produto'].map(renderFilterItem)}
            </FilterList>
          </FilterSection>
          <FilterFooter>
            <i /> integração entre departamentos
          </FilterFooter>
          <FilterActions>
            <button
              type="button"
              onClick={() => setHiddenTypes(new Set(['cliente', 'fornecedor', 'produto']))}
            >
              Só operação
            </button>
            <button type="button" onClick={() => setHiddenTypes(new Set())}>
              Tudo
            </button>
          </FilterActions>
          </FilterPanel>

        <Panel $open={!!selected} aria-hidden={!selected}>
          {selected ? (
            <>
              <PanelHead $color={selected.color}>
                <i />
                <div>
                  <small>{TYPE_LABELS[selected.type] || 'Nó'}</small>
                  <strong title={selected.label}>{selected.label}</strong>
                </div>
                <button type="button" onClick={() => setSelectedId(null)} aria-label="Fechar painel">
                  <CloseOutlined />
                </button>
              </PanelHead>
              <PanelBody>
                {selected.type === 'departamento' ? (
                  <PanelMeta>
                    <span>Atividades</span>
                    <b>{selected.count}</b>
                    <span>Nível</span>
                    <b>{selected.level || maxLevel} de 5</b>
                    <span>Situação</span>
                    <b>{(selected.level || maxLevel) > 1 ? 'Aberto' : 'Recolhido'}</b>
                    {selected.intOut ? (
                      <>
                        <span>Integrações de saída</span>
                        <b>{selected.intOut}</b>
                      </>
                    ) : null}
                    {selected.intIn ? (
                      <>
                        <span>Integrações de entrada</span>
                        <b>{selected.intIn}</b>
                      </>
                    ) : null}
                  </PanelMeta>
                ) : null}

                {selected.type === 'atividade' && !(selectedRaw && selectedRaw.__more) ? (
                  <PanelMeta>
                    {selected.sub ? (
                      <>
                        <span>Etapa</span>
                        <b>{selected.sub}</b>
                      </>
                    ) : null}
                    {selectedRaw && selectedRaw.client && selectedRaw.client.name ? (
                      <>
                        <span>Cliente</span>
                        <b>{selectedRaw.client.name}</b>
                      </>
                    ) : null}
                    {selectedRaw && selectedRaw.budgetAmount ? (
                      <>
                        <span>Valor</span>
                        <b>R$ {formatMoney(String(selectedRaw.budgetAmount))}</b>
                      </>
                    ) : null}
                    {selectedRaw && selectedRaw.controlNumber ? (
                      <>
                        <span>Controle</span>
                        <b>#{selectedRaw.controlNumber}</b>
                      </>
                    ) : null}
                  </PanelMeta>
                ) : null}

                {selected.type === 'atividade' && selectedRaw && selectedRaw.__more ? (
                  <PanelMeta>
                    <span>Mostrando</span>
                    <b>
                      {selectedRaw.shown} de {selectedRaw.total}
                    </b>
                    <span>Motivo</span>
                    <b>Limite de performance</b>
                  </PanelMeta>
                ) : null}

                {selected.type === 'cliente' ? (
                  <PanelMeta>
                    {selected.data && selected.data.cpf_cnpj ? (
                      <>
                        <span>Documento</span>
                        <b>{selected.data.cpf_cnpj}</b>
                      </>
                    ) : null}
                    {selected.data && selected.data.fone ? (
                      <>
                        <span>Telefone</span>
                        <b>{selected.data.fone}</b>
                      </>
                    ) : null}
                    {selected.data && selected.data.email ? (
                      <>
                        <span>E-mail</span>
                        <b>{selected.data.email}</b>
                      </>
                    ) : null}
                  </PanelMeta>
                ) : null}

                {selected.type === 'produto' ? (
                  <PanelMeta>
                    {selected.data && selected.data.quantity ? (
                      <>
                        <span>Quantidade</span>
                        <b>{selected.data.quantity}</b>
                      </>
                    ) : null}
                    {selected.data && selected.data.unit_price ? (
                      <>
                        <span>Valor unitário</span>
                        <b>R$ {formatMoney(String(selected.data.unit_price))}</b>
                      </>
                    ) : null}
                  </PanelMeta>
                ) : null}

                {selected.type === 'tarefa' ? (
                  <PanelMeta>
                    {selected.data && selected.data.status ? (
                      <>
                        <span>Status</span>
                        <b>{selected.data.status}</b>
                      </>
                    ) : null}
                    {selected.data && valueName(selected.data.item) ? (
                      <>
                        <span>Item</span>
                        <b>{valueName(selected.data.item)}</b>
                      </>
                    ) : null}
                    {selected.data && valueName(selected.data.realizedBy) ? (
                      <>
                        <span>Fornecedor</span>
                        <b>{valueName(selected.data.realizedBy)}</b>
                      </>
                    ) : null}
                    {selected.data && selected.data.qtd ? (
                      <>
                        <span>Quantidade</span>
                        <b>{selected.data.qtd}</b>
                      </>
                    ) : null}
                    {selected.data && selected.data.activitie_id ? (
                      <>
                        <span>Encadeia</span>
                        <b>outra atividade</b>
                      </>
                    ) : null}
                  </PanelMeta>
                ) : null}

                {selected.type === 'fornecedor' ? (
                  <PanelMeta>
                    {selected.data && selected.data.cpf_cnpj ? (
                      <>
                        <span>Documento</span>
                        <b>{selected.data.cpf_cnpj}</b>
                      </>
                    ) : null}
                    {selected.data && selected.data.fone ? (
                      <>
                        <span>Telefone</span>
                        <b>{selected.data.fone}</b>
                      </>
                    ) : null}
                    {selected.data && selected.data.email ? (
                      <>
                        <span>E-mail</span>
                        <b>{selected.data.email}</b>
                      </>
                    ) : null}
                  </PanelMeta>
                ) : null}

                <div>
                  {selected.type === 'departamento' ? (
                    <Tag>nível {selected.level || maxLevel}</Tag>
                  ) : null}
                  {selected.type === 'atividade' ? <Tag>atividade</Tag> : null}
                  {selected.type === 'tarefa' ? <Tag>tarefa da OS</Tag> : null}
                  {selected.type === 'cliente' ? <Tag>cliente</Tag> : null}
                  {selected.type === 'fornecedor' ? <Tag>fornecedor</Tag> : null}
                  {selected.type === 'produto' ? <Tag>produto</Tag> : null}
                </div>

                <ActionRow>
                  {(selected.type === 'atividade' || selected.type === 'tarefa') && !(selectedRaw && selectedRaw.__more) ? (
                    <ActionButton type="button" onClick={() => graphRef.current && graphRef.current.focus(selected.id)}>
                      <AimOutlined /> Centralizar
                    </ActionButton>
                  ) : null}

                  {(selected.type === 'cliente' || selected.type === 'fornecedor') && canOpenPeople && selected.data && valueId(selected.data) ? (
                    <ActionButton type="button" onClick={() => openPeople(valueId(selected.data))}>
                      Abrir cadastro <ArrowRightOutlined />
                    </ActionButton>
                  ) : null}

                  {selected.type === 'produto' && canOpenProduct && selected.data && selected.data.item ? (
                    <ActionButton type="button" onClick={() => openProduct(selected.data.item)}>
                      Abrir produto <ArrowRightOutlined />
                    </ActionButton>
                  ) : null}

                  {selected.type === 'departamento' && selectedBoardId ? (
                    <ActionButton type="button" className="ghost" onClick={() => openBoard(selectedBoardId)}>
                      Abrir o quadro <ArrowRightOutlined />
                    </ActionButton>
                  ) : null}

                  {selected.type === 'atividade' && selectedBoardId ? (
                    <ActionButton
                      type="button"
                      className="ghost"
                      onClick={() => {
                        const activityId =
                          selected.data && selected.data._id
                            ? selected.data._id
                            : selected.data && selected.data.__stub
                              ? selected.id.replace('act:', '')
                              : null;
                        if (activityId) openActivity(selectedBoardId, activityId);
                        else openBoard(selectedBoardId);
                      }}
                    >
                      Abrir atividade <ArrowRightOutlined />
                    </ActionButton>
                  ) : null}

                  {selected.type === 'tarefa' && selectedBoardId ? (
                    <ActionButton
                      type="button"
                      className="ghost"
                      onClick={() => {
                        if (selected.activityId) openActivity(selectedBoardId, selected.activityId);
                        else openBoard(selectedBoardId);
                      }}
                    >
                      Abrir atividade <ArrowRightOutlined />
                    </ActionButton>
                  ) : null}
                </ActionRow>

              </PanelBody>
            </>
          ) : null}
        </Panel>

        </RightRail>

        <Controls $panel={false}>
          <Tooltip title="Aproximar" placement="left">
            <ControlButton type="button" onClick={() => graphRef.current && graphRef.current.zoom(1.25)}>
              <PlusOutlined />
            </ControlButton>
          </Tooltip>
          <Tooltip title="Afastar" placement="left">
            <ControlButton type="button" onClick={() => graphRef.current && graphRef.current.zoom(0.8)}>
              <MinusOutlined />
            </ControlButton>
          </Tooltip>
          <Tooltip title="Enquadrar" placement="left">
            <ControlButton type="button" onClick={() => graphRef.current && graphRef.current.fit()}>
              <AimOutlined />
            </ControlButton>
          </Tooltip>
          <Tooltip title={spinning ? 'Pausar giro' : 'Girar'} placement="left">
            <ControlButton
              type="button"
              onClick={() => {
                const on = graphRef.current && graphRef.current.toggleSpin();
                setSpinning(!!on);
              }}
            >
              <SyncOutlined spin={spinning} />
            </ControlButton>
          </Tooltip>
          <Tooltip title="Reorganizar" placement="left">
            <ControlButton type="button" onClick={() => graphRef.current && graphRef.current.reheat()}>
              <ReloadOutlined />
            </ControlButton>
          </Tooltip>
          <Tooltip title="Recolher tudo" placement="left">
            <ControlButton type="button" onClick={collapseAll}>
              <CloseOutlined />
            </ControlButton>
          </Tooltip>
        </Controls>



        {loading ? (
          <CenterOverlay>
            <Spinner />
            Conectando aos dados da operação…
          </CenterOverlay>
        ) : null}

        {!loading && error ? (
          <Empty>
            <strong>Não deu para montar o mapa</strong>
            <span>{error}</span>
            <ActionButton type="button" onClick={loadDepartments} style={{ marginTop: 10 }}>
              <ReloadOutlined /> Tentar de novo
            </ActionButton>
          </Empty>
        ) : null}

        {!loading && !error && !departments.length ? (
          <Empty>
            <strong>Nenhum departamento disponível</strong>
            <span>
              O mapa mostra apenas os departamentos que você já acessa na tela de Quadros. Se precisar
              de outro departamento, peça a liberação ao administrador.
            </span>
          </Empty>
        ) : null}
      </Wrap>
    </>
  );
}
